import React, { useState, useMemo } from "react";
import { Checkbox, FormControl, Box, FormControlLabel } from "@mui/material";
import Select, { SelectChangeEvent } from "@mui/material/Select";

import Button from "components/button";
import TextField from "components/textField";

import {
  filterBoxStyle,
  filterListStyle,
  filterNoData,
  MenuProps,
  selectCompStyles,
} from "./index.styles";
import { filterSearchField } from "features/businessListing/index.styles";
import { MultiSelectPropType } from "../index.types";
import { DropDownList } from "utils/types";

const FiltersDropDown: React.FC<MultiSelectPropType> = ({
  value = [],
  dropDownList,
  onChange,
  onChangeButton,
  sx,
  disabled,
  disableButton,
}) => {
  const [openOptions, setOpenOptions] = useState<boolean>(false);
  const [searchQuery, setSearchQuery] = useState<string>("");

  // ==============================|| handler functions ||============================== //

  // memoize the filtered options to prevent recalculating on every render
  const filteredOptions: DropDownList[] = useMemo(() => {
    return dropDownList?.filter((option) =>
      option?.label?.toLowerCase()?.includes(searchQuery?.toLowerCase())
    );
  }, [searchQuery, dropDownList]);

  // function to set the search results
  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setSearchQuery(event?.target?.value);
  };

  // checkbox handler function
  const handleCheckboxChange =
    (label: string) =>
    (event: React.ChangeEvent<HTMLInputElement>): void => {
      const selectedValues: string[] = [...value];

      // add the label if checked
      if (event.target.checked) {
        selectedValues.push(label);
      } else {
        // remove the label if unchecked
        const index: number = selectedValues.indexOf(label);

        if (index > -1) {
          selectedValues.splice(index, 1);
        }
      }

      // pass value in callback
      onChange?.({
        target: { value: selectedValues },
      } as SelectChangeEvent<string[]>);
    };

  // ==============================|| UI ||============================== //

  return (
    <FormControl
      disabled={disabled}
      sx={{ "& .MuiOutlinedInput-notchedOutline": { border: "unset" } }}
    >
      <Select
        value={value}
        open={openOptions}
        onOpen={() => setOpenOptions(true)}
        onClose={() => setOpenOptions(false)}
        multiple
        renderValue={(selected) =>
          selected.length === 0 ? "Empty" : selected.join(", ")
        }
        MenuProps={MenuProps}
        sx={{ ...selectCompStyles, ...sx }}
      >
        {/* validate list length to display search field */}
        {dropDownList?.length > 1 && (
          // wrapped in box to properly display the searched value
          <Box>
            <TextField
              type="search"
              value={searchQuery}
              placeholder="Search"
              onChange={handleSearchChange}
              sx={filterSearchField}
            />
          </Box>
        )}

        {/* render the filtered options based on search query */}
        {filteredOptions?.length ? (
          filteredOptions?.map((option) => (
            <FormControlLabel
              key={option?.label}
              label={option?.label}
              control={
                <Checkbox
                  checked={value?.indexOf(option?.label) > -1}
                  onChange={handleCheckboxChange(option?.label)}
                />
              }
              sx={filterListStyle}
            />
          ))
        ) : (
          <Box sx={filterNoData}>No results found</Box>
        )}

        {/* apply button */}
        <Box sx={filterBoxStyle}>
          <Button
            variant="contained"
            color="secondary"
            sx={{ width: "100%", p: "12px 0", borderRadius: "4px" }}
            onClick={(event) => {
              setOpenOptions(false);
              onChangeButton(event);
            }}
            disabled={disableButton || !filteredOptions?.length}
          >
            Apply
          </Button>
        </Box>
      </Select>
    </FormControl>
  );
};

export default FiltersDropDown;
